import React, {useCallback, useEffect, useState} from 'react';
import {
  View,
  Image,
  ViewStyle,
  TextStyle,
  ImageStyle,
  StyleSheet,
} from 'react-native';
import {Formik, FormikProps} from 'formik';
import {MultiSelect, Button, TextInput, TextArea, DatePicker, Text} from 'ui-kits';
import colors from 'theme/Colors';
import {spacing, corner_radius} from 'theme/Units';
import {AddProjectValidation} from 'utils/schema';
import {projectTeamMember} from 'redux/slices/projectSlice';
import {defaultUserProfile} from 'constants/DefaultValues';
import {
  getEmployeesRequest,
  getEmployeesRequestParams,
  Employee,
} from 'redux/slices/employeeSlice';
import {getOrgID} from 'redux/selectors/authSelector';
import {
  employeeNextPage,
  allEmployeesList,
} from 'redux/selectors/employeeSelector';
import {useDispatch, useSelector} from 'react-redux';

type propTypes = Readonly<{
  initialValues: {
    id: string;
    proj_id?: string;
    name: string;
    description: string;
    start_date: Date | null;
    end_date: Date | null;
    sprint_duration: string;
    team_member?: projectTeamMember[];
  };
  isEditing?: boolean;
  onSubmit: (values: any) => void;
  onCancel: () => void;
}>;

const renderItem = (item: Employee) => (
  <View style={styles.renderItemWrapper}>
    <Image
      style={styles.renderItemImage}
      source={{
        uri: item.profile_photo_url ?? defaultUserProfile,
      }}
    />
    <View style={styles.renderItemInfo}>
      <Text>
        {item.first_name} {item.last_name}
      </Text>
    </View>

    <View style={styles.engagedWrapper}>
      <Text style={styles.engagedLabel}>Engaged</Text>
      <Text style={styles.engagedNumber}>{item.project?.length}</Text>
    </View>
  </View>
);

export function ProjectForm(props: propTypes) {
  const {initialValues, isEditing = false, onSubmit, onCancel} = props;

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={AddProjectValidation}
      onSubmit={onSubmit}>
      {formikProp => (
        <FormComponent
          {...formikProp}
          initialValues={initialValues}
          isEditing={isEditing}
          onCancel={onCancel}
        />
      )}
    </Formik>
  );
}

type formProps = Readonly<{
  initialValues: propTypes['initialValues'];
  isEditing: boolean;
  onCancel: () => void;
}> &
  FormikProps<propTypes['initialValues']>;
function FormComponent({
  handleChange,
  handleBlur,
  handleSubmit,
  values,
  errors,
  setFieldValue,
  dirty,
  isValid,
  setValues,
  initialValues,
  isEditing,
  onCancel,
}: formProps) {
  const org_id = useSelector(getOrgID);
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const allEmployees = useSelector(allEmployeesList);
  const nextEmployeePage = useSelector(employeeNextPage);

  useEffect(() => {
    if (initialValues && isEditing) {
      // de-serialize the values
      setValues(initialValues);
    }
  }, [initialValues, setValues, isEditing]);

  const fetchEmployee = useCallback(() => {
    const payload: getEmployeesRequestParams = {
      org_id: org_id as any,
      page: currentPage,
    };

    dispatch(getEmployeesRequest(payload));
  }, [dispatch, org_id, currentPage]);

  useEffect(() => {
    // if employee list is empty then fetch them
    fetchEmployee();
  }, [fetchEmployee]);

  function fetchNextPage() {
    if (nextEmployeePage) {
      setCurrentPage(nextEmployeePage);
    }
  }

  return (
    <View style={styles.form}>
      <View style={styles.formFirstSection}>
        {isEditing ? (
          <TextInput
            placeholder="Project ID"
            label="Project ID"
            value={values.id}
            disabled={true}
          />
        ) : null}
        <TextInput
          placeholder="Enter Project Name"
          label="Project Name"
          required
          maxLength={50}
          value={values.name}
          error={errors.name}
          onChangeText={handleChange('name')}
          onBlur={handleBlur('name')}
        />
        <TextArea
          placeholder="Enter Project Description"
          label="Project Description"
          value={values.description}
          error={errors.description}
          onChangeText={handleChange('description')}
        />
      </View>
      <View style={styles.formSecondSection}>
        <DatePicker
          label="Start Date"
          calenderStyle={styles.startDateCalender}
          value={values.start_date}
          error={errors.start_date as string}
          onChangeDate={date => setFieldValue('start_date', date)}
          onBlur={date => setFieldValue('start_date', date)}
        />
        <DatePicker
          label="End Date"
          calenderStyle={styles.endDateCalender}
          value={values.end_date}
          error={errors.end_date as string}
          onChangeDate={date => setFieldValue('end_date', date)}
          onBlur={date => setFieldValue('end_date', date)}
        />
        {!isEditing ? (
          <MultiSelect
            label="Team Member"
            keyExtractor={(item: Employee) => item.id.toString()}
            options={allEmployees}
            searchKeyExtractor={(item: Employee) =>
              item.first_name! + item.last_name! + item.id
            }
            renderItem={renderItem}
            onSave={selected => {
              setFieldValue('projectTeamMember', selected);
            }}
            selectedEntries={values.team_member}
            searchPlaceholder="Search..."
            placeholder="Add Member"
            dropDownStyle={styles.dropDownStyle}
            searchHeader="Select Team Member"
            fetchNextHandler={fetchNextPage} // pass the next fetch handler
          />
        ) : null}
        <TextInput
          placeholder="14 days"
          label="Sprint Duration"
          type="numeric"
          value={values.sprint_duration}
          error={errors.sprint_duration}
          onChangeText={handleChange('sprint_duration')}
          maxLength={2}
        />
      </View>

      <View style={styles.actionButtonWrapper}>
        <Button
          onPress={onCancel}
          text="Cancel"
          variant="secondary"
          style={styles.actionButton}
        />
        <Button
          onPress={handleSubmit}
          text={isEditing ? 'Update' : 'Create Project'}
          variant="primary"
          disabled={!isValid || !dirty}
          style={styles.actionButton}
          textStyle={styles.submitButtonText}
        />
      </View>
    </View>
  );
}

type styleType = {
  form: ViewStyle;
  icon: ImageStyle;
  actionButton: ViewStyle;
  actionButtonWrapper: ViewStyle;
  formFirstSection: ViewStyle;
  formSecondSection: ViewStyle;
  submitButtonText: TextStyle;
  renderItemWrapper: ViewStyle;
  renderItemImage: ImageStyle;
  renderItemInfo: ViewStyle;
  engagedNumber: TextStyle;
  engagedWrapper: ViewStyle;
  engagedLabel: TextStyle;
  dropDownStyle: ViewStyle;
  endDateCalender: ViewStyle;
  startDateCalender: ViewStyle;
};

const styles: styleType = StyleSheet.create({
  endDateCalender: {
    right: 0,
    bottom: '81%',
  },
  startDateCalender: {
    left: '12.5%',
    bottom: '81%',
  },
  dropDownStyle: {
    minHeight: 350,
    bottom: '20%',
    width: 350,
    left: '0%',
  },
  renderItemWrapper: {flexDirection: 'row', gap: spacing['2'], minWidth: 250},
  renderItemImage: {width: 40, height: 40, borderRadius: 999},
  renderItemInfo: {gap: spacing['1']},

  submitButtonText: {color: colors.palette.white},
  formFirstSection: {gap: spacing['6']},
  formSecondSection: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: '1fr 1fr',
    columnGap: spacing['6'],
    rowGap: spacing['4'],
  },
  engagedLabel: {
    backgroundColor: colors.palette.brandBlue,
    color: colors.palette.white,
    fontWeight: 500,
    paddingVertical: 2,
    paddingHorizontal: spacing['1'],
    paddingRight: spacing['3'],
    fontSize: 12,
    borderRadius: corner_radius['rounded-md'],
  },
  engagedWrapper: {
    marginLeft: 'auto',
    flexDirection: 'row',
    position: 'relative',
    alignItems: 'center',
  },
  engagedNumber: {
    backgroundColor: colors.palette.brandPink,
    color: colors.palette.white,
    borderRadius: 999,
    fontSize: 10,
    borderWidth: 2,
    borderColor: colors.palette.white,
    paddingHorizontal: spacing['1'],
    position: 'relative',
    transform: 'translate(-9px, 0px)',
  },
  form: {
    flex: 1,
    gap: spacing['4'],
    paddingVertical: spacing['4'],
  },
  actionButton: {
    minWidth: 0,
    width: '100%',
  },
  actionButtonWrapper: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: spacing['6'],
    justifyContent: 'center',
    zIndex: -10,
    width: '100%',
  },
  icon: {
    width: 14,
    height: 14,
    resizeMode: 'contain',
  },
});
